'use client';

import {
  BROWSER_UPLOAD_PUT_TRANSPORT,
  browserUploadCleanupSignal,
  browserUploadPutHeaders,
  hashBrowserUploadBody,
  isStoredByEarlierPut,
  newBrowserUploadAttemptId,
  ownsBrowserUploadSession,
  parseBrowserUploadControl,
  postBrowserUploadControl,
  type BrowserUploadBegun,
  type BrowserUploadDirectControl,
  withDirectBrowserUploadControl,
} from './browser-upload-session-client';
import type { BrowserUploadIdentity } from './browser-upload-hash';

export function isUploadProxyConfigured(): boolean {
  return (
    process.env.NODE_ENV === 'development' ||
    Boolean(process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL)
  );
}

export function uploadProxyRequestUrl(
  proxyUrl: string,
  signedUrl: string
): string {
  const isRelative = proxyUrl.startsWith('/');
  const url = new URL(
    proxyUrl,
    isRelative ? 'http://upload-proxy.local' : undefined
  );
  url.searchParams.set('url', signedUrl);
  return isRelative
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString();
}

async function uploadDirect(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  signal: AbortSignal
): Promise<Response> {
  return fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
    signal,
  });
}

async function uploadBrowserSession(
  control: NonNullable<ReturnType<typeof parseBrowserUploadControl>>,
  body: Blob | File,
  contentType: string,
  signal: AbortSignal,
  precomputedIdentity?: Promise<BrowserUploadIdentity>
): Promise<Response> {
  const attemptId = newBrowserUploadAttemptId();
  const identity = await (precomputedIdentity ?? hashBrowserUploadBody(body));
  // A failed begin throws here, before anything this call owns exists.
  const begun = await postBrowserUploadControl<BrowserUploadBegun>(
    control,
    {
      operation: 'begin',
      contentType,
      attemptId,
      ...identity,
      ...BROWSER_UPLOAD_PUT_TRANSPORT,
    },
    signal
  );
  // Abort only a session this attempt began: a replayed begin from another
  // caller of the same session must never delete that caller's upload.
  const abortOwnedSession = async () => {
    if (!ownsBrowserUploadSession(begun)) return;
    await postBrowserUploadControl(
      control,
      { operation: 'abort', attemptId },
      browserUploadCleanupSignal()
    ).catch(() => undefined);
  };
  try {
    const part = await uploadLegacy(
      begun.uploadUrl,
      body,
      contentType,
      signal,
      browserUploadPutHeaders(begun),
      false
    );
    const stored = isStoredByEarlierPut(begun, part.status);
    if (!part.ok && !stored) {
      await abortOwnedSession();
      return part;
    }
    const partEtag = part.headers.get('etag');
    if (!partEtag && !begun.uploadHeaders)
      throw new Error('Upload part did not return an ETag.');
    await postBrowserUploadControl(
      control,
      {
        operation: 'complete',
        ...(partEtag ? { partEtag } : {}),
        ...(begun.uploadHeaders ? BROWSER_UPLOAD_PUT_TRANSPORT : {}),
      },
      signal
    );
    return new Response(null, { status: 200, statusText: 'OK' });
  } catch (error) {
    await abortOwnedSession();
    throw error;
  }
}

function rejectionMessage(bodyText: string): string {
  try {
    const payload: unknown = JSON.parse(bodyText);
    if (typeof payload !== 'object' || payload === null) return '';
    const error = 'error' in payload ? payload.error : undefined;
    const message = 'message' in payload ? payload.message : undefined;
    if (typeof error === 'string') return error;
    return typeof message === 'string' ? message : '';
  } catch {
    return '';
  }
}

/**
 * Whether a proxy response means the PROXY could not carry this upload (it is
 * down, or the signed URL's bucket is outside the prefixes that worker
 * allows), so the same signed PUT should go straight to storage instead. This
 * reads the response, never the build environment: a deployment whose bucket
 * the worker allows never takes the direct path.
 */
export function isProxyRejectionForDirectRetry(
  status: number,
  bodyText: string
): boolean {
  if (status >= 500 && status < 600) return true;
  if (status !== 400) return false;
  const message = rejectionMessage(bodyText);
  return (
    message === 'URL not in allowed prefixes' ||
    message.includes('ALLOWED_URL_PREFIXES')
  );
}

async function shouldRetryDirectUpload(response: Response): Promise<boolean> {
  if (response.status !== 400 && !(response.status >= 500)) return false;
  return isProxyRejectionForDirectRetry(
    response.status,
    await response
      .clone()
      .text()
      .catch(() => '')
  );
}

/**
 * The one signed storage PUT of a browser upload session, through the upload
 * proxy with every signed header passed verbatim. No direct-to-storage retry:
 * a session PUT is either proxied or rejected. Times out like `uploadViaProxy`
 * so a stalled proxy or R2 becomes a retryable failure.
 */
export async function putSignedBrowserUpload(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  signal: AbortSignal,
  signedHeaders: Record<string, string>,
  timeoutMs = 10 * 60 * 1000
): Promise<Response> {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal.aborted) controller.abort();
  else signal.addEventListener('abort', onAbort, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await uploadLegacy(
      signedUrl,
      body,
      contentType,
      controller.signal,
      signedHeaders,
      false
    );
  } catch (error) {
    if (timedOut) throw new Error('Upload timed out. Please try again.');
    throw error;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', onAbort);
  }
}

async function uploadLegacy(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  signal: AbortSignal,
  signedHeaders: Record<string, string> = {},
  allowDirectFallback = true
): Promise<Response> {
  const configuredProxyUrl = process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL;
  const proxyUrl =
    process.env.NODE_ENV === 'development'
      ? '/api/upload-proxy'
      : configuredProxyUrl;
  const headers = { 'Content-Type': contentType, ...signedHeaders };
  if (!proxyUrl)
    return fetch(signedUrl, { method: 'PUT', headers, body, signal });
  const response = await fetch(uploadProxyRequestUrl(proxyUrl, signedUrl), {
    method: 'PUT',
    headers,
    body,
    signal,
  });
  if (allowDirectFallback && (await shouldRetryDirectUpload(response)))
    return fetch(signedUrl, { method: 'PUT', headers, body, signal });
  return response;
}

/**
 * Upload a file via the upload proxy worker.
 *
 * The proxy forwards the upload to the signed R2 URL, solving CORS issues
 * and providing consistent upload behavior across all browsers.
 *
 * @param signedUrl - The R2 signed upload URL
 * @param body - The file/blob to upload
 * @param contentType - The MIME type of the file
 * @param options - Optional timeout/abort control
 * @returns The fetch response from the upload
 */
export async function uploadViaProxy(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  options?: {
    timeoutMs?: number;
    signal?: AbortSignal;
    /** A browser-session identity already being hashed (e.g. in parallel with the presign call). */
    identity?: Promise<BrowserUploadIdentity>;
    /** Run session control on the caller's Convex client (skips the route hop). */
    directControl?: BrowserUploadDirectControl;
  }
): Promise<Response> {
  const configuredProxyUrl = process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL;
  // Browsers may not trust or be able to resolve a developer's Tailscale
  // upload host. Keep local uploads same-origin and let the guarded Next route
  // forward the already-signed request to R2 without a browser CORS preflight.
  const proxyUrl =
    process.env.NODE_ENV === 'development'
      ? '/api/upload-proxy'
      : configuredProxyUrl;
  const uploadBody =
    process.env.NODE_ENV === 'development' &&
    typeof File !== 'undefined' &&
    body instanceof File
      ? new Blob([await body.arrayBuffer()], { type: contentType })
      : body;
  const browserControl = withDirectBrowserUploadControl(
    parseBrowserUploadControl(signedUrl),
    options?.directControl
  );

  const controller = new AbortController();
  // Default is intentionally generous because this helper is used for non-image uploads too.
  // Call sites that want faster user feedback (e.g. image uploads) should override it.
  const timeoutMs = options?.timeoutMs ?? 10 * 60 * 1000; // 10 minutes
  const timeoutId =
    timeoutMs > 0
      ? setTimeout(() => {
          controller.abort();
        }, timeoutMs)
      : undefined;

  const externalSignal = options?.signal;
  const onAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', onAbort, { once: true });
  }

  // If no proxy URL configured, fall back to direct upload
  try {
    if (browserControl)
      return await uploadBrowserSession(
        browserControl,
        uploadBody,
        contentType,
        controller.signal,
        options?.identity
      );
    if (!proxyUrl) {
      return await uploadDirect(
        signedUrl,
        uploadBody,
        contentType,
        controller.signal
      );
    }

    // Upload via proxy using `url` query param
    const proxyRequestUrl = uploadProxyRequestUrl(proxyUrl, signedUrl);

    if (process.env.NODE_ENV === 'development') {
      const signed = new URL(signedUrl);
      console.warn('[upload-via-proxy] dispatching local upload', {
        proxyUrl,
        signedOrigin: signed.origin,
        signedBucketPath: signed.pathname.split('/').slice(0, 2).join('/'),
        signedUrlLength: signedUrl.length,
      });
    }

    const proxiedResponse = await fetch(proxyRequestUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: uploadBody,
      signal: controller.signal,
    });

    if (await shouldRetryDirectUpload(proxiedResponse)) {
      controller.signal.throwIfAborted();
      return await uploadDirect(
        signedUrl,
        uploadBody,
        contentType,
        controller.signal
      );
    }

    return proxiedResponse;
  } catch (err) {
    // Normalize timeouts/aborts into a friendly error message
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Upload timed out. Please try again.');
    }
    if (browserControl) throw err;
    if (proxyUrl && !controller.signal.aborted) {
      try {
        return await uploadDirect(
          signedUrl,
          uploadBody,
          contentType,
          controller.signal
        );
      } catch (directError) {
        const proxyMessage = err instanceof Error ? err.message : String(err);
        const directMessage =
          directError instanceof Error
            ? directError.message
            : String(directError);
        throw new Error(
          `Upload proxy ${proxyUrl} failed (${proxyMessage}); direct upload also failed (${directMessage}).`
        );
      }
    }
    throw err;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener('abort', onAbort);
  }
}
